import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShellProvider, Shell,
  Spotlight, SpotlightInput, SpotlightResults, SpotlightSection, SpotlightItem, SpotlightEmpty,
} from '@ofself/shell';
import DeveloperMark from './dev/DeveloperMark';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import { useNotifications } from '../contexts/NotificationContext';

import { SECTIONS, placeOf } from '../devmap';
import { PhoneBar, useIsPhone } from './dev/PhoneNav';
import api from '../services/api';
import './EcoShell.css';

/**
 * The desk's own chrome reaches the shell's search through this, and learns
 * whether there is anyone signed in to have an inbox. Kit 0.317 stopped drawing
 * either: a custom `railItems` replaces the default rail (and with it the search
 * seat), and the dock inbox is gone. The location line places both, as the mockup
 * does; the inbox itself is DevInbox.
 */
const PortalChrome = createContext({ openSearch: null, signedIn: false });
export const usePortalChrome = () => useContext(PortalChrome);

// The ecosystem shell for the developer portal — the same frame Nucleus
// stands (frontend/src/components/Layout.js):
//
//   the APP RAIL   the mark (Home), then one door per section of devmap.js —
//                  a glyph over its word — then the avatar's cell
//   the DOCK       the avatar raises the kit's PlatformDock: spaces, the
//                  fleet, the inbox, help, the two ways out
//   the STAGE      PortalLayout's page column and desk
//
// The portal used to hand-build all of that (a portaled avatar seat, its own
// console, a bottom-left corner cluster, three walls nothing opened). The kit
// carries it now, so none of it is copied here to drift.

// The spotlight's census: every page on the map, plus the words a developer
// types for Money that are not its label.
const SEARCH_INDEX = [
  ...SECTIONS.flatMap((s) => s.pages.map((p) => ({ section: s.label, label: p.label, to: p.to }))),
  { section: 'Money', label: 'Payouts and earnings', to: '/billing' },
  { section: 'Apps', label: 'API docs', to: '/api-docs' },
];

// THE MARK. The kit's register has no drawn figure for `developer` (AppMark
// renders nothing for it), so the Home cell stood empty. The portal's own
// mark: its accent square, the braces of code in it.
const DEV_MARK = <DeveloperMark size={34} />;

/**
 * Is the payout account mid-setup? Lights Money's badge until Stripe is satisfied.
 *
 * Onboarding finishes on Stripe's site, in another tab or after a redirect, so a
 * read taken once per mount kept the badge lit after can_charge turned true. It is
 * read again when the developer comes back to the portal (focus, or the tab
 * becoming visible) and whenever they land on Money (/billing).
 *
 * It also listens to every payout-account answer the api layer receives
 * (`api.onPayoutAccount`), so the Money page's fresh read — the one that sees Stripe's
 * new state — turns the badge off the moment it lands, not on the next focus.
 */
export function usePayoutPending(activeGroupId, authed, pathname) {
  const [pending, setPending] = useState(false);
  // Only the latest read may land: a slow answer for the space you just left, or
  // one arriving after unmount, is dropped.
  const latest = useRef(0);
  useEffect(() => () => { latest.current = -1; }, []);

  const check = useCallback(() => {
    const ticket = ++latest.current;
    if (!authed) { setPending(false); return; }
    api.getPayoutAccount(activeGroupId || undefined)
      .then((res) => { if (latest.current === ticket) setPending(!!res?.account && !res.account.can_charge); })
      .catch(() => { if (latest.current === ticket) setPending(false); });
  }, [activeGroupId, authed]);

  // Someone else's read for this owner landed (the Money page's fresh one, say): take
  // it, and drop any read of ours still in flight, which may be the older copy. A
  // dropped shared copy means "ask again".
  useEffect(() => {
    if (!authed) return undefined;
    const owner = activeGroupId || null;
    return api.onPayoutAccount((event) => {
      if (event?.invalidated) { check(); return; }
      if (!event || event.groupId !== owner) return;
      latest.current += 1;
      const account = event.response?.account;
      setPending(!!account && !account.can_charge);
    });
  }, [activeGroupId, authed, check]);

  const onMoney = pathname === '/billing';
  useEffect(() => { check(); }, [check, onMoney]);

  useEffect(() => {
    if (!authed) return undefined;
    const onBack = () => { if (!document.hidden) check(); };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      window.removeEventListener('focus', onBack);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [authed, check]);
  return pending;
}

export default function EcoShell({ children }) {
  const { user, logout } = useAuth();
  const { groups, activeGroup, activeGroupId, switchToGroup, switchToPersonal } = useGroup();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = placeOf(location.pathname, location.search);
  const payoutPending = usePayoutPending(activeGroupId, !!user, location.pathname);
  const phone = useIsPhone();

  // The user's uploaded personal avatar (self:profile → avatar_file_id), wired
  // the same way nucleus's Layout does.
  const [avatarUrl, setAvatarUrl] = useState(null);
  const refreshAvatar = useCallback(async () => {
    if (!user) { setAvatarUrl(null); return; }
    try {
      const schema = await api.getSchemaByUri('profile').catch(() => null)
        || await api.getSchemaByUri('self:profile').catch(() => null);
      if (!schema?.id) { setAvatarUrl(null); return; }
      const res = await api.getNodes({ schema_id: schema.id, limit: 1 });
      const node = (res?.nodes || res || [])[0];
      const fileId = node?.value_json?.avatar_file_id;
      if (!fileId) { setAvatarUrl(null); return; }
      const dl = await api.getNodeDownloadUrl(fileId);
      setAvatarUrl(dl?.download_url || null);
    } catch { /* no avatar set */ }
  }, [user]);
  useEffect(() => { refreshAvatar(); }, [refreshAvatar]);

  const eco = useMemo(() => ({
    currentUser: user ? { username: user.username, email: user.email, avatarUrl } : null,
    groups: groups || [],
    activeGroup: activeGroup || null,
    onSwitchSpace: (s) => {
      if (s && s.id) switchToGroup(s.id);
      else switchToPersonal();
    },
    onHelp: () => navigate('/documentation'),
    // The kit's dock switcher still reads the unread count for this app's badge.
    // The inbox itself (rows, lanes, answers) is DevInbox, in the location line.
    notificationInbox: { unreadCount },
    // Two doors, two scopes. `logout()` defaults to 'global', so wiring the
    // plain one to it signed you out of every app in the fleet.
    onSignOut: () => logout({ scope: 'app' }),
    onSignOutEcosystem: () => logout({ scope: 'global' }),
  }), [user, avatarUrl, groups, activeGroup, switchToGroup, switchToPersonal, navigate, logout, unreadCount]);

  // THE DOORS: each section's glyph over its word, the door you are behind lit.
  const railItems = useMemo(() => ['logo', ...SECTIONS.map((s) => {
    const Glyph = s.glyph;
    return {
      id: s.id,
      label: s.label,
      glyph: (
        <span className="nx-rail-glyph">
          <Glyph size={15} strokeWidth={1.5} />
          <span className="nx-rail-word">{s.label}</span>
        </span>
      ),
      active: section?.id === s.id,
      onClick: () => navigate(s.pages[0].to),
      badge: s.id === 'money' && payoutPending ? 1 : undefined,
    };
  })], [section, navigate, payoutPending]);

  // ── ⌘K — the portal's page census, filtered live. ──
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const q = query.trim().toLowerCase();
  const hits = q
    ? SEARCH_INDEX.filter((n) => n.label.toLowerCase().includes(q) || n.section.toLowerCase().includes(q))
    : SEARCH_INDEX;
  const sections = [...new Set(hits.map((h) => h.section))];
  const go = (to) => { setSearchOpen(false); setQuery(''); navigate(to); };

  const chrome = useMemo(() => ({ openSearch: () => setSearchOpen(true), signedIn: !!user }), [user]);

  return (
    <PortalChrome.Provider value={chrome}>
    <ShellProvider value={eco}>
      <Shell
        currentApp="developer"
        mark={DEV_MARK}
        rail
        dock
        onHome={() => navigate('/home')}
        onSearch={() => setSearchOpen(true)}
        activeSeat={section?.id || 'home'}
        railItems={railItems}
        settings={[]}
      >
        <div className="eco-stage">{children}</div>
        {phone && (
          <PhoneBar
            sections={SECTIONS}
            active={section?.id || null}
            onHome={() => navigate('/home')}
            onGo={(s) => navigate(s.pages[0].to)}
          />
        )}

        <Spotlight open={searchOpen} onClose={() => { setSearchOpen(false); setQuery(''); }} label="Search the portal">
          <SpotlightInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the portal"
            autoFocus
          />
          <SpotlightResults>
            {sections.map((s) => (
              <SpotlightSection key={s} title={s}>
                {hits.filter((h) => h.section === s).map((h) => (
                  <SpotlightItem key={`${h.label}-${h.to}`} onClick={() => go(h.to)}>{h.label}</SpotlightItem>
                ))}
              </SpotlightSection>
            ))}
            {!hits.length && <SpotlightEmpty>Nothing matches.</SpotlightEmpty>}
          </SpotlightResults>
        </Spotlight>
      </Shell>
    </ShellProvider>
    </PortalChrome.Provider>
  );
}
